import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { Button } from '@/components/ui/button';
import { X, ChevronRight, ChevronLeft, Sparkles, Hand } from 'lucide-react';

const OnboardingTour = ({ user }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [spotlightRect, setSpotlightRect] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const tooltipRef = useRef(null);
  const { isUSA } = useLocale();

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  // Desktop steps - targets sidebar elements with correct translated labels
  const desktopSteps = [
    {
      target: '[data-testid="dashboard-welcome"]',
      title: isUSA ? 'Welcome to TexHub!' : '¡Bienvenido a TexHub!',
      content: isUSA 
        ? "We'll guide you step by step to get the most out of the platform. It will only take 1 minute."
        : 'Te guiaremos paso a paso para que aproveches al máximo la plataforma. Solo tomará 1 minuto.',
      position: 'bottom',
    },
    {
      target: isUSA ? '[data-testid="nav-catalog"]' : '[data-testid="nav-catálogo"]',
      title: isUSA ? 'Your Product Catalog' : 'Tu Catálogo de Productos',
      content: isUSA 
        ? 'Upload and manage your products here. Add photos, prices, and technical details.'
        : 'Sube y administra tus productos aquí. Agrega fotos, precios y detalles técnicos.',
      position: 'right',
    },
    {
      target: isUSA ? '[data-testid="nav-suppliers"]' : '[data-testid="nav-proveedores"]',
      title: isUSA ? 'Find Suppliers' : 'Busca Proveedores',
      content: isUSA 
        ? 'Search for verified suppliers of fabrics, threads, trims and more.'
        : 'Busca proveedores verificados de telas, hilos, avíos y más.',
      position: 'right',
    },
    {
      target: isUSA ? '[data-testid="nav-community-blog"]' : '[data-testid="nav-blog-comunitario"]',
      title: isUSA ? 'Community Blog' : 'Blog Comunitario',
      content: isUSA 
        ? 'Stay updated with industry news, trends and community posts.'
        : 'Mantente al día con noticias, tendencias y publicaciones de la comunidad.',
      position: 'right',
    },
    {
      target: isUSA ? '[data-testid="nav-my-team"]' : '[data-testid="nav-mi-equipo"]',
      title: isUSA ? 'My Team' : 'Mi Equipo',
      content: isUSA 
        ? 'Invite your team members to collaborate on your company account.'
        : 'Invita a los miembros de tu equipo para colaborar en tu cuenta de empresa.',
      position: 'right',
    },
    {
      target: '[data-testid="nav-texvision-ai"]',
      title: 'TexVision AI',
      content: isUSA 
        ? 'Generate professional product images using AI. Perfect for your catalog.'
        : 'Genera imágenes profesionales de productos usando IA. Perfectas para tu catálogo.',
      position: 'right',
    },
    {
      target: isUSA ? '[data-testid="nav-online-store"]' : '[data-testid="nav-tienda-en-línea"]',
      title: isUSA ? 'Your Online Store' : 'Tu Tienda en Línea',
      content: isUSA 
        ? 'Create your own branded store to showcase your products to buyers. This is where you can start selling!'
        : 'Crea tu propia tienda con tu marca para mostrar tus productos a compradores. ¡Aquí es donde puedes empezar a vender!',
      position: 'right',
    },
  ];

  // Mobile steps - simplified flow with correct targets
  const mobileSteps = [
    {
      target: '[data-testid="dashboard-welcome"]',
      title: isUSA ? 'Welcome to TexHub!' : '¡Bienvenido a TexHub!',
      content: isUSA 
        ? 'We\'ll guide you step by step. Swipe left to continue or tap "Next".'
        : 'Te guiaremos paso a paso. Desliza hacia la izquierda para continuar o toca "Siguiente".',
      position: 'bottom',
      mobilePosition: 'center',
    },
    {
      target: 'header.lg\\:hidden button:first-child',
      title: isUSA ? 'Main Menu' : 'Menú Principal',
      content: isUSA 
        ? 'Tap here to access: Catalog, Suppliers, Blog, My Team, TexVision AI, and your Online Store.'
        : 'Toca aquí para acceder a: Catálogo, Proveedores, Blog, Mi Equipo, TexVision AI, y tu Tienda en Línea.',
      position: 'bottom',
      mobilePosition: 'bottom',
    },
    {
      target: '[data-testid="quick-action-products"]',
      title: isUSA ? 'Your Catalog' : 'Tu Catálogo',
      content: isUSA 
        ? 'Upload your products with photos and prices. Buyers will find them when searching.'
        : 'Sube tus productos con fotos y precios. Los compradores los encontrarán cuando busquen.',
      position: 'top',
      mobilePosition: 'top',
    },
    {
      target: '[data-testid="quick-action-search"]',
      title: isUSA ? 'Find Suppliers' : 'Buscar Proveedores',
      content: isUSA 
        ? 'Search for verified suppliers of fabrics, threads, trims and more.'
        : 'Busca proveedores verificados de telas, hilos, avíos y más.',
      position: 'top',
      mobilePosition: 'top',
    },
    {
      target: '[data-testid="mobile-nav-store"]',
      title: isUSA ? 'Your Online Store' : 'Tu Tienda en Línea',
      content: isUSA 
        ? 'Create your store to showcase products and receive orders directly!'
        : '¡Crea tu tienda para mostrar productos y recibir pedidos directamente!',
      position: 'top',
      mobilePosition: 'top',
    },
    {
      target: '[data-testid="dashboard-welcome"]',
      title: isUSA ? 'Ready to start!' : '¡Listo para empezar!',
      content: isUSA 
        ? 'You now know the basics. Explore all features from the menu. Good luck!'
        : 'Ya conoces lo básico. Explora todas las funciones desde el menú. ¡Mucho éxito!',
      position: 'bottom',
      mobilePosition: 'center',
    },
  ];

  // Memoize steps to prevent infinite re-renders
  const steps = useMemo(() => isMobile ? mobileSteps : desktopSteps, [isMobile, isUSA]);

  // Check screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Check if user should see the tour
  useEffect(() => {
    if (!user?.email) return;
    
    const hasCompletedTour = localStorage.getItem(`texhub_tour_completed_${user.email}`);
    
    if (!hasCompletedTour && location.pathname === '/dashboard') {
      const timer = setTimeout(() => {
        setIsActive(true);
        setCurrentStep(0);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [user?.email, location.pathname]);

  // Handle touch events for swipe
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe && currentStep > 0) {
      handlePrev();
    }
  };

  // Update tooltip and spotlight position
  useEffect(() => {
    if (!isActive) return;

    const updatePosition = () => {
      const step = steps[currentStep];
      if (!step) return;
      
      const targetElement = document.querySelector(step.target);
      
      if (!targetElement) {
        // If target not found on mobile, show centered tooltip
        if (isMobile) {
          setSpotlightRect(null);
          setTooltipPosition({
            top: window.innerHeight / 2 - 100,
            left: window.innerWidth / 2 - 150,
          });
        }
        return;
      }

      const rect = targetElement.getBoundingClientRect();
      const padding = isMobile ? 4 : 8;
      
      // Set spotlight rectangle
      setSpotlightRect({
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
      });

      // Calculate tooltip position
      const tooltipWidth = isMobile ? Math.min(300, window.innerWidth - 32) : 340;
      const tooltipHeight = isMobile ? 180 : 200;
      let top, left;

      if (isMobile) {
        // Mobile: Always position tooltip centered or at specific positions
        const mobilePos = step.mobilePosition || 'center';
        
        switch (mobilePos) {
          case 'top':
            top = 80;
            left = (window.innerWidth - tooltipWidth) / 2;
            break;
          case 'bottom':
            top = window.innerHeight - tooltipHeight - 100;
            left = (window.innerWidth - tooltipWidth) / 2;
            break;
          case 'center':
          default:
            top = (window.innerHeight - tooltipHeight) / 2;
            left = (window.innerWidth - tooltipWidth) / 2;
            break;
        }
      } else {
        // Desktop positioning
        switch (step.position) {
          case 'right':
            top = rect.top + rect.height / 2 - tooltipHeight / 2;
            left = rect.right + 20;
            break;
          case 'left':
            top = rect.top + rect.height / 2 - tooltipHeight / 2;
            left = rect.left - tooltipWidth - 20;
            break;
          case 'top':
            top = rect.top - tooltipHeight - 20;
            left = rect.left + rect.width / 2 - tooltipWidth / 2;
            break;
          case 'bottom':
          default:
            top = rect.bottom + 20;
            left = rect.left + rect.width / 2 - tooltipWidth / 2;
            break;
        }

        // Keep tooltip within viewport
        top = Math.max(20, Math.min(top, window.innerHeight - tooltipHeight - 20));
        left = Math.max(20, Math.min(left, window.innerWidth - tooltipWidth - 20));
      }

      setTooltipPosition({ top, left });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    // Don't listen to scroll events - causes performance issues
    // window.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
    };
  }, [isActive, currentStep, isMobile]);

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTour();
    }
  }, [currentStep, steps.length]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const handleSkip = () => {
    completeTour();
  };

  const completeTour = () => {
    if (user?.email) {
      localStorage.setItem(`texhub_tour_completed_${user.email}`, 'true');
    }
    setIsActive(false);
  };

  if (!isActive) return null;

  const tooltipWidth = isMobile ? Math.min(300, window.innerWidth - 32) : 340;

  return (
    <div 
      className="fixed inset-0 z-[9999]" 
      data-testid="onboarding-tour"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Overlay with spotlight hole */}
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {spotlightRect && (
              <rect
                x={spotlightRect.left}
                y={spotlightRect.top}
                width={spotlightRect.width}
                height={spotlightRect.height}
                rx="8"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(15, 23, 42, 0.8)"
          mask="url(#spotlight-mask)"
          style={{ pointerEvents: 'auto' }}
          onClick={(e) => e.stopPropagation()}
        />
      </svg>

      {/* Spotlight glow effect */}
      {spotlightRect && (
        <div
          className="absolute rounded-lg pointer-events-none"
          style={{
            top: spotlightRect.top - 4,
            left: spotlightRect.left - 4,
            width: spotlightRect.width + 8,
            height: spotlightRect.height + 8,
            boxShadow: '0 0 0 4px rgba(5, 150, 105, 0.4), 0 0 20px rgba(5, 150, 105, 0.3)',
            borderRadius: '12px',
          }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={`absolute bg-white rounded-xl shadow-2xl border border-slate-200 z-[10000] ${
          isMobile ? 'p-4' : 'p-5'
        }`}
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          width: tooltipWidth,
          animation: 'fadeIn 0.2s ease-out',
        }}
      >
        {/* Close button */}
        <button
          onClick={handleSkip}
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 transition-colors p-1"
          aria-label={isUSA ? 'Close tour' : 'Cerrar tour'}
        >
          <X className={isMobile ? 'w-5 h-5' : 'w-5 h-5'} />
        </button>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className={`text-emerald-600 ${isMobile ? 'w-4 h-4' : 'w-5 h-5'}`} />
          <span className={`text-slate-500 ${isMobile ? 'text-xs' : 'text-sm'}`}>
            {isUSA ? `Step ${currentStep + 1} of ${steps.length}` : `Paso ${currentStep + 1} de ${steps.length}`}
          </span>
        </div>

        {/* Content */}
        <h3 
          className={`font-bold text-slate-900 mb-2 ${isMobile ? 'text-base' : 'text-lg'}`} 
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          {steps[currentStep]?.title}
        </h3>
        <p className={`text-slate-600 leading-relaxed mb-4 ${isMobile ? 'text-xs' : 'text-sm'}`}>
          {steps[currentStep]?.content}
        </p>

        {/* Progress bar */}
        <div className="flex gap-1 mb-3">
          {steps.map((_, index) => (
            <div
              key={index}
              className={`h-1 flex-1 rounded-full transition-colors ${
                index <= currentStep ? 'bg-emerald-600' : 'bg-slate-200'
              }`}
            />
          ))}
        </div>

        {/* Swipe hint for mobile */}
        {isMobile && (
          <div className="flex items-center justify-center gap-2 text-xs text-slate-400 mb-3">
            <Hand className="w-3 h-3" />
            <span>{isUSA ? 'Swipe to navigate' : 'Desliza para navegar'}</span>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex items-center justify-between">
          <button
            onClick={handleSkip}
            className={`text-slate-400 hover:text-slate-600 transition-colors ${isMobile ? 'text-xs' : 'text-sm'}`}
          >
            {isUSA ? 'Skip' : 'Saltar'}
          </button>
          <div className="flex items-center gap-2">
            {currentStep > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrev}
                className={isMobile ? 'h-8 px-2 text-xs' : 'h-9'}
              >
                <ChevronLeft className={isMobile ? 'w-3 h-3' : 'w-4 h-4 mr-1'} />
                {!isMobile && (isUSA ? 'Previous' : 'Anterior')}
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleNext}
              className={`bg-emerald-600 hover:bg-emerald-700 ${isMobile ? 'h-8 px-3 text-xs' : 'h-9'}`}
            >
              {currentStep === steps.length - 1 ? (
                isUSA ? "Let's go!" : '¡Empezar!'
              ) : (
                <>
                  {isUSA ? 'Next' : 'Siguiente'}
                  <ChevronRight className={isMobile ? 'w-3 h-3 ml-1' : 'w-4 h-4 ml-1'} />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* CSS for animation */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default OnboardingTour;
